import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue, off, getDatabase, set } from 'firebase/database';
import app from '../config/firebase';
import './AccordingToGamePage.css';

const database = getDatabase(app);

function AccordingToGamePage({ API_URL }) {
  const { code } = useParams();
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // Who the current player voted for
  const [selectedAnswerer, setSelectedAnswerer] = useState(null); // Which player is answering the question
  const [answererVoted, setAnswererVoted] = useState(false); // Has the answerer made their choice
  const [correctAnswer, setCorrectAnswer] = useState(null); // The player ID that the answerer chose
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [roundStartTime, setRoundStartTime] = useState(null);
  const [viewingPhase, setViewingPhase] = useState('voting'); // voting, answerer, results
  const VOTING_DURATION = 60; // Seconds for voting

  const [isHost, setIsHost] = useState(false);
  
  const [gameState, setGameState] = useState({
    currentQuestion: "Who is most likely to become a millionaire?",
    players: [
      { id: 1, name: 'John', hasVoted: false, votes: 2 },
      { id: 2, name: 'Alice', hasVoted: true, votes: 1 },
      { id: 3, name: 'Bob', hasVoted: false, votes: 0 },
      { id: 4, name: 'Emma', hasVoted: true, votes: 1 },
    ]
  });

  // Listen for real-time game state updates
  useEffect(() => {
    const partyRef = ref(database, `parties/${code}`);
    let previousRoundNumber = null;
    
    // Set up real-time listener
    onValue(partyRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setError('Party not found');
        setIsLoading(false);
        return;
      }

      if (data.gameFinished) {
        navigate(`/analysis/${code}`);
        return;
      }

      if (!data.gameStarted && !data.gameFinished) {
        navigate(`/lobby/${code}`);
        return;
      }

      console.log('Game state:', data);

      // Check if question has changed
      if (previousRoundNumber !== null && previousRoundNumber !== data.currentRound) {
        setShowResults(false);
        setTimeLeft(30);
        setSelectedPlayer(null);
        setViewingPhase('voting');
        setAnswererVoted(false);
        setCorrectAnswer(null);
      }
      previousRoundNumber = data.currentRound;
      
      // Get the selected answerer for this round from Firebase
      if (data.accordingToAnswerer) {
        setSelectedAnswerer(data.accordingToAnswerer);
      }
      
      // Check if the answerer has voted and capture their answer as the correct one
      if (data.players && data.accordingToAnswerer && data.players[data.accordingToAnswerer]) {
        const answerer = data.players[data.accordingToAnswerer];
        if (answerer.hasVoted && !answererVoted) {
          setAnswererVoted(true);
          // If the answerer has voted, record their choice as the correct answer
          if (answerer.votedFor) {
            setCorrectAnswer(answerer.votedFor);
            setViewingPhase('results');
          }
        }
      }

      // Update round start time if available
      if (data.roundStartTime) {
        setRoundStartTime(data.roundStartTime);
        
        // Calculate remaining time based on server timestamp
        const currentTime = Date.now();
        const elapsedSeconds = Math.floor((currentTime - data.roundStartTime) / 1000);
        const remainingTime = Math.max(0, VOTING_DURATION - elapsedSeconds);
        
        // If time is already up but results aren't shown, show them
        if (remainingTime <= 0 && !showResults) {
          setTimeLeft(0);
          setShowResults(true);
        } else if (!showResults) {
          // Only update the timer if we're not already showing results
          setTimeLeft(remainingTime);
        }
      }
      
      // Check if all players have voted to show results early
      const players = Object.entries(data.players || {}).map(([id, player]) => ({
        id,
        name: player.name,
        hasVoted: player.hasVoted || false,
        votes: player.votes || 0,
        totalVotesReceived: player.totalVotesReceived || 0
      }));
      
      // In According To mode, we show results when either:
      // 1. Everyone has voted, OR
      // 2. The answerer has voted (their vote is the "correct" answer)
      const allPlayersVoted = players.length > 0 && players.every(player => player.hasVoted);
      const currentPlayerIsAnswerer = data.accordingToAnswerer === localStorage.getItem('playerId');
      
      if (allPlayersVoted && !showResults && timeLeft > 0) {
        console.log('All players have voted, showing results early');
        setShowResults(true);
        setViewingPhase('results');
      }
      
      // If the answerer has voted, record their answer but don't show results yet
      if (data.players && data.accordingToAnswerer && 
          data.players[data.accordingToAnswerer] && 
          data.players[data.accordingToAnswerer].hasVoted &&
          data.players[data.accordingToAnswerer].votedFor &&
          !correctAnswer) {
        setCorrectAnswer(data.players[data.accordingToAnswerer].votedFor);
        setAnswererVoted(true);
        // Don't immediately show results - wait for all players to vote
        // The all-players-voted check above will handle showing results
      }
      
      setGameState({
        currentQuestion: data.currentQuestion,
        currentRound: data.currentRound,
        players: Object.entries(data.players || {}).map(([id, player]) => ({
          id,
          name: player.name,
          hasVoted: player.hasVoted || false,
          votes: player.votes || 0,
          votedFor: player.votedFor || null,
          totalVotesReceived: player.totalVotesReceived || 0,
          score: player.score || 0 // Track scores for According To mode
        }))
      });
      setIsHost(data.info?.hostId === localStorage.getItem('playerId'));
      setError(null);
      setIsLoading(false);
    });

    // Clean up listener when component unmounts
    return () => {
      off(partyRef);
    };
  }, [code, navigate]);

  // Timer effect - synchronized with server timestamp
  useEffect(() => {
    if (roundStartTime && timeLeft > 0 && !showResults) {
      const timer = setTimeout(() => {
        // Recalculate time based on server timestamp on each tick
        const currentTime = Date.now();
        const elapsedSeconds = Math.floor((currentTime - roundStartTime) / 1000);
        const remainingTime = Math.max(0, VOTING_DURATION - elapsedSeconds);
        
        setTimeLeft(remainingTime);
        
        if (remainingTime <= 0) {
          setShowResults(true);
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [timeLeft, showResults, roundStartTime]);

  const getPlayerInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const handleVote = async (playerId) => {
    const currentPlayerId = localStorage.getItem('playerId');
    const currentPlayerObject = gameState.players.find(player => player.id === currentPlayerId);
    
    // Don't allow voting if the player has already voted or voting is in progress
    if (timeLeft <= 0 || selectedPlayer || (currentPlayerObject && currentPlayerObject.hasVoted) || votingInProgress) {
      return;
    }
    
    // Special handling for the answerer
    const isAnswerer = selectedAnswerer === currentPlayerId;
    
    setVotingInProgress(true);
    try {
      // Use a specialized endpoint for According To mode
      const response = await fetch(`${API_URL}/parties/${code}/vote-according-to`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: currentPlayerId,
          votedForId: playerId,
          roundId: gameState.currentRound,
          isAnswerer: isAnswerer
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSelectedPlayer(playerId);
      
      // If this is the answerer voting, save their answer as the correct one
      // but don't immediately show results
      if (isAnswerer) {
        setCorrectAnswer(playerId);
        setAnswererVoted(true);
        // Don't set viewing phase or show results yet
        // Let the Firebase listener handle showing results when all have voted
      }
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError('Failed to submit vote. Please try again.');
    }
    setVotingInProgress(false);
  };

  const handleFinishGame = async () => {
    setIsLoading(true);
    if (!isHost) {
      setError('Only the host can finish the game');
      return;
    }

    try {
      // Use the dedicated According To finish endpoint
      const response = await fetch(`${API_URL}/parties/${code}/according-to-finish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      navigate(`/analysis/${code}`);
    } catch (err) {
      console.error('Error finishing game:', err);
      setError('Failed to finish game. Please try again.');
    }
    setIsLoading(false);
  };

  const handleNextQuestion = async () => {
    setIsLoading(true);
    if (!isHost) {
      setError('Only the host can move to the next question');
      return;
    }

    try {
      // Use the dedicated According To next question endpoint
      const response = await fetch(`${API_URL}/parties/${code}/according-to-next-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Reset UI state
      setShowResults(false);
      setSelectedPlayer(null);
      setViewingPhase('voting');
      setAnswererVoted(false);
      setCorrectAnswer(null);
      setError(null);
      // Don't manually set timeLeft as it will be calculated from the server timestamp
    } catch (err) {
      console.error('Error moving to next question:', err);
      setError('Failed to move to next question. Please try again.');
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-container">
        <div className="game-content">
          <div className="error-message">{error}</div>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Format the question with "According to [player]" prefix
  const formatQuestion = () => {
    if (!selectedAnswerer) return gameState.currentQuestion;
    
    const answererName = gameState.players.find(p => p.id === selectedAnswerer)?.name || 'Someone';
    return `According to ${answererName.toUpperCase()}, ${gameState.currentQuestion}`;
  };
  
  // Determine if a player got the correct answer (matches answerer's choice)
  const playerGotCorrectAnswer = (playerId) => {
    if (!correctAnswer || !gameState.players) return false;
    
    const player = gameState.players.find(p => p.id === playerId);
    return player && player.votedFor === correctAnswer;
  };
  
  return (
    <div className="game-container">
      <div className="game-content">
        <div className="question-card">
          {!showResults ? <div className="timer">{timeLeft}s</div> : null}
          <h2 className="question-text">{formatQuestion()}</h2>
          
          {selectedAnswerer === localStorage.getItem('playerId') && viewingPhase === 'voting' && !answererVoted && (
            <div className="answerer-notice">You are answering this question! Your answer will be the correct one.</div>
          )}
          
          {answererVoted && !showResults && (
            <div className="answerer-voted-notice">The answerer has voted! Waiting for all players to vote...</div>
          )}
          
          {!showResults ? (
            <div className="players-grid">
              {gameState.players.map(player => (
                <div
                  key={player.id}
                  className={`player-card ${selectedPlayer === player.id ? 'selected' : ''} ${player.id === selectedAnswerer ? 'answerer' : ''}`}
                  onClick={() => handleVote(player.id)}
                >
                  <div className="player-avatar">
                    {getPlayerInitials(player.name)}
                  </div>
                  <div className="player-name">{player.name}</div>
                  <div className="vote-status">
                    {player.id === selectedAnswerer ? (
                      <span className="answerer-badge">Answerer</span>
                    ) : (
                      player.hasVoted ? 'Voted' : 'Thinking...'
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="results-grid">
                {correctAnswer && (
                  <div className="correct-answer-banner">
                    <strong>{gameState.players.find(p => p.id === selectedAnswerer)?.name.toUpperCase()}</strong> chose <strong>{gameState.players.find(p => p.id === correctAnswer)?.name.toUpperCase()}</strong>
                  </div>
                )}
                
                {gameState.players
                  .sort((a, b) => {
                    // First sort by whether they got the answer correct
                    const aCorrect = playerGotCorrectAnswer(a.id) ? 1 : 0;
                    const bCorrect = playerGotCorrectAnswer(b.id) ? 1 : 0;
                    
                    if (aCorrect !== bCorrect) return bCorrect - aCorrect;
                    
                    // Then sort by score
                    return b.score - a.score;
                  })
                  .map((player, index) => {
                    const isCorrect = playerGotCorrectAnswer(player.id);
                    const isAnswerer = player.id === selectedAnswerer;
                    
                    return (
                      <div
                        key={player.id}
                        className={`result-card ${isCorrect ? 'correct' : ''} ${isAnswerer ? 'answerer' : ''}`}
                      >
                        <div className="result-avatar">
                          {getPlayerInitials(player.name)}
                        </div>
                        <div className="result-name">{player.name}</div>
                        {isAnswerer && <div className="answerer-label">Answerer</div>}
                        <div className="result-status">
                          {isAnswerer ? (
                            'Selected the answer'
                          ) : isCorrect ? (
                            <div className="correct-badge">+1 Correct!</div>
                          ) : (
                            'Incorrect'
                          )}
                        </div>
                        <div className="score">
                          Score: {player.score}
                        </div>
                      </div>
                    );
                  })}
              </div>
              {showResults && isHost && (
                <div className="host-controls">
                  <button className="finish-button" onClick={handleFinishGame}>
                    Finish & Analyze Game
                  </button>
                  <button className="next-button" onClick={handleNextQuestion}>
                    Next Question
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccordingToGamePage;
